// Preview of a related word (docs/project.md §7): a synonym or antonym tapped in a card that is
// not in this dictionary. The card comes from the shared base (GET /dictionaries/{id}/lookup),
// usually built ahead of time by the worker; "Add to dictionary" is the ordinary add. Online only:
// the local copy holds only the lexemes the dictionary's entries reference (docs/db.md §8).
import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { mergeEntry, type Card } from '@solungo/core/merge';
import { headwordLower } from '@solungo/core/lang';
import type { EntryRow, LookupResponse } from '@solungo/core/schema';
import { api } from '@/api/endpoints';
import { useCards } from '@/db/hooks';
import { useDictionary, useUser } from '@/state/session';
import { useOnline } from '@/state/online';
import { useIsDesktop } from '@/lib/useIsDesktop';
import { addWord } from '@/state/pending';
import { CardView } from '@/components/CardView';
import { Sheet, SheetBar, useCloseOverlay, useOverlayChain } from '@/components/ui';

export const Route = createFileRoute('/_app/d/$dictionaryId/l/$headword')({
  component: PreviewScreen,
});

type Found = LookupResponse & { lexeme: NonNullable<LookupResponse['lexeme']> };

// The basics, then the details: the preview keeps polling until both are in
const isComplete = (r: LookupResponse) =>
  r.lexeme !== null &&
  r.lexeme.detailsFilledAt !== null &&
  r.gloss !== null &&
  r.gloss.detailsFilledAt !== null;

// The shared rows as a card without an entry: nothing personal, no overrides. The synthetic entry
// never reaches Dexie or the server.
function previewCard(dictionaryId: string, r: Found): Card {
  const entry: EntryRow = {
    id: `preview:${r.lexeme.id}`,
    dictionaryId,
    lexemeId: r.lexeme.id,
    createdBy: null,
    transcription: null,
    partOfSpeech: null,
    translations: null,
    examples: null,
    related: null,
    createdAt: r.lexeme.createdAt,
    updatedAt: r.lexeme.updatedAt,
    deletedAt: null,
    syncSeq: '0',
  };
  return mergeEntry(r.lexeme, r.gloss, entry);
}

function PreviewScreen() {
  const { dictionaryId, headword } = Route.useParams();
  const t = useTranslations('card');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const online = useOnline();
  const desktop = useIsDesktop();
  const user = useUser();
  const dictionary = useDictionary(dictionaryId);
  const cards = useCards(dictionary);
  const chain = useOverlayChain();
  const close = useCloseOverlay({ to: '/d/$dictionaryId', params: { dictionaryId } }, chain + 1);
  const headwordIndex = useMemo(() => new Map((cards ?? []).map((c) => [c.headwordLower, c.id])), [cards]);
  // The word is in the dictionary after all (the local copy knows): its entry is the card to show
  const localEntryId = dictionary ? headwordIndex.get(headwordLower(headword, dictionary.targetLang)) : undefined;

  const lookup = useQuery({
    queryKey: ['lookup', dictionaryId, headword],
    queryFn: ({ signal }) => api.entries.lookup(dictionaryId, headword, signal),
    enabled: online && !!dictionary && !localEntryId,
    // a card still being built lands within seconds: poll until it is complete
    refetchInterval: (q) => (q.state.data?.lexeme && !isComplete(q.state.data) ? 3000 : false),
    retry: 1,
    staleTime: 0,
  });
  const entryId = localEntryId ?? lookup.data?.entryId ?? null;
  useEffect(() => {
    // the entry's card takes the preview's place in history, keeping the chain depth
    if (entryId) void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId }, replace: true, state: true });
  }, [entryId, dictionaryId, navigate]);

  const [adding, setAdding] = useState(false);
  const onAdd = () => {
    if (adding) return;
    setAdding(true);
    // The exact spelling of the base when known, so the add hits the same lexeme
    void addWord(dictionaryId, lookup.data?.lexeme?.headword ?? headword, 'preview').then((out) => {
      if (out.entryId) {
        // In the dictionary now (built, or being built): its own card takes the place of the preview
        void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId: out.entryId }, replace: true, state: true });
      } else {
        // Failed: the list shows the error row with a retry
        close();
      }
    });
  };

  if (!dictionary) return null;
  const found = lookup.data?.lexeme ? (lookup.data as Found) : null;
  const card = found ? previewCard(dictionaryId, found) : null;
  const title = card?.headword ?? headword;
  const message = (text: string) => <div className="p-6 text-[15px] text-ink-secondary">{text}</div>;

  let body: React.ReactNode;
  if (entryId) body = null;
  else if (!online) body = message(t('previewOffline'));
  else if (lookup.isError) {
    body = (
      <div className="flex flex-col items-start gap-3 p-6 text-[15px] text-ink-secondary">
        <span>{tc('errorGeneric')}</span>
        <button type="button" className="font-semibold text-accent" onClick={() => void lookup.refetch()}>
          {tc('retry')}
        </button>
      </div>
    );
  } else if (!lookup.data) body = message(tc('loading'));
  else if (card) {
    body = (
      <CardView
        card={card}
        dictionary={dictionary}
        user={user}
        authorName={null}
        headwordIndex={headwordIndex}
        online={online}
        desktop={desktop}
        onEdit={() => undefined}
        onDelete={() => undefined}
        onRetryFill={() => undefined}
        preview={{ onAdd, adding }}
        chain={chain}
      />
    );
  } else {
    // Not in the base: the add creates the placeholder and the worker builds the card
    body = (
      <div className="flex flex-col gap-5 px-5 pb-8 pt-2 desktop:px-7 desktop:pt-[26px]" data-testid="preview-unknown">
        <h1 className="m-0 text-[36px] font-bold leading-[42px] tracking-[-0.5px] desktop:text-[40px] desktop:leading-[46px]" lang={dictionary.targetLang}>
          {headword}
        </h1>
        <p className="m-0 text-[15px] leading-[21px] text-ink-secondary">{t('notInBase')}</p>
        <button
          type="button"
          disabled={adding}
          onClick={onAdd}
          data-testid="preview-add"
          className="flex h-[50px] items-center justify-center rounded-[12px] bg-accent px-5 text-[17px] font-semibold text-ink-inverse disabled:opacity-60 desktop:h-9 desktop:self-start desktop:rounded-lg desktop:text-[13px]"
        >
          {t('addToDictionary')}
        </button>
      </div>
    );
  }

  return (
    <Sheet onClose={close} label={t('title', { word: title })} tone="surface" width="max-w-[940px]" desktopHeight="desktop:h-[min(680px,calc(100vh-80px))]">
      <div className="desktop:border-b desktop:border-separator">
        <SheetBar onClose={close} closeLabel={t('title', { word: title })} right={<div className="w-11" />} />
      </div>
      {/* keyed by the word: another preview in the same sheet starts scrolled to the top */}
      <div key={headword} className="flex min-h-0 flex-grow flex-col overflow-y-auto desktop:overflow-hidden" data-testid="preview">
        {body}
      </div>
    </Sheet>
  );
}
